import React, { FunctionComponent, ReactNode } from 'react'

import styled from 'styled-components/native'

import { BackButtonV2 } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/backButton/BackButtonV2'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

const Container = styled.View({
  width: '100%',
})

const Header = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['16px'],
  backgroundColor: theme.colors.backgroundPrimary,
  padding: theme.spacing['24px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const HeaderText = styled.View(({ theme }) => ({
  flexShrink: 1,
  gap: theme.spacing['4px'],
}))

const ContentContainer = styled.View(({ theme }) => ({
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing['24px'],
}))

const Content = styled.View(({ theme }) => ({
  width: '100%',
  maxWidth: '1120px',
  gap: theme.spacing['24px'],
}))

const CardContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['24px'],
  gap: theme.spacing['16px'],
  borderRadius: '16px',
  borderWidth: '1px',
  borderColor: theme.colors.borderDefault,
  backgroundColor: theme.colors.backgroundPrimary,
}))

type AdminPageLayoutProps = {
  title: string
  subtitle?: string
  /** Shows a back button in the header when provided. */
  onBack?: () => void
  testID?: string
  children: ReactNode
}

/** Shared page frame for the admin area: a header band with an optional back button, and centered content. */
export const AdminPageLayout: FunctionComponent<AdminPageLayoutProps> = ({
  title,
  subtitle,
  onBack,
  testID,
  children,
}) => (
  <Container testID={testID}>
    <Header>
      {onBack && <BackButtonV2 onPress={onBack} testID={tID('AdminPageLayout-back')} />}
      <HeaderText>
        <Title size={TitleSize.LARGE} level='1' text={title} testID={testID ? `${testID}-title` : undefined} />
        {subtitle && <BodyText text={subtitle} size={BodyTextSize.DEFAULT} />}
      </HeaderText>
    </Header>
    <ContentContainer>
      <Content>{children}</Content>
    </ContentContainer>
  </Container>
)

type AdminCardProps = {
  title?: string
  description?: string
  children?: ReactNode
}

/** Bordered card used to group a block of content on admin pages. */
export const AdminCard: FunctionComponent<AdminCardProps> = ({ title, description, children }) => (
  <CardContainer>
    {title && <Title size={TitleSize.MEDIUM} level='2' text={title} />}
    {description && <BodyText text={description} />}
    {children}
  </CardContainer>
)
